<template>
    <Dialog :visible="visible" :modal="true" :maximizable="true"
            :style="{width: '90vw'}" :contentStyle="{height: '75vh', paddingLeft: '10px'}"
            :header="$t('AppOperationsCenterFieldsPreviewDialog.Importar talhões do Operations Center')"
            @update:visible="$emit('update:visible', $event)"
            @show="onShow" @hide="onHide">

        <div v-if="notLinked" class="oc-message">
            <i class="pi pi-info-circle" style="font-size: 2rem; margin-bottom: 12px;"></i>
            <div>{{ $t('AppOperationsCenterFieldsPreviewDialog.Nenhuma organização do Operations Center vinculada') }}</div>
            <small>{{ $t('AppOperationsCenterFieldsPreviewDialog.Vincule uma organização em Configurações') }}</small>
        </div>

        <div v-else class="preview-body">
            <!-- Mapa -->
            <div class="preview-map-wrapper">
                <div id="oc-preview-map" ref="previewMap"></div>
                <div v-if="loading" class="map-loading-overlay">
                    <ProgressSpinner style="width: 50px; height: 50px"/>
                </div>
            </div>

            <!-- Controles -->
            <div class="preview-controls">
                <div class="controls-summary">
                    <span>{{ selectedCount }} {{ $t('AppOperationsCenterFieldsPreviewDialog.selecionados') }}</span>
                    <span v-if="total" class="total-summary">
                        · {{ total }} {{ $t('AppOperationsCenterFieldsPreviewDialog.talhões na organização') }}
                    </span>
                    <span v-if="pageSummary" class="total-summary"> · {{ pageSummary }}</span>
                </div>

                <div class="preview-table-scroll">
                    <DataTable :value="rows" dataKey="id"
                               class="p-datatable-sm preview-table" :rowClass="rowClass"
                               @row-mouseenter="onRowHover">
                        <template #empty>
                            <span v-if="!loading">{{ $t('AppOperationsCenterFieldsPreviewDialog.Nenhum talhão nesta página') }}</span>
                        </template>
                        <Column :headerStyle="{width: '3rem'}">
                            <template #header>
                                <Checkbox :binary="true" v-model="allSelectedOnPage" :disabled="selectableCount === 0"
                                          @change="toggleAllOnPage"/>
                            </template>
                            <template #body="slotProps">
                                <Checkbox :binary="true" v-model="slotProps.data.selected"
                                          :disabled="!canSelect(slotProps.data)"
                                          @change="onSelectionChange(slotProps.data)"/>
                            </template>
                        </Column>
                        <Column :header="$t('AppOperationsCenterFieldsPreviewDialog.Nome')">
                            <template #body="slotProps">
                                <span>{{ slotProps.data.name || $t('AppOperationsCenterFieldsPreviewDialog.(sem nome)') }}</span>
                                <small v-if="slotProps.data.partCount > 1" class="part-hint">
                                    {{ $t('AppOperationsCenterFieldsPreviewDialog.parte') }}
                                    {{ slotProps.data.partIndex }}/{{ slotProps.data.partCount }}
                                </small>
                                <Tag v-if="slotProps.data.importable && slotProps.data.duplicate" severity="warning" class="ml-2"
                                     :value="$t('AppOperationsCenterFieldsPreviewDialog.Já existe')"/>
                            </template>
                        </Column>
                        <Column :header="$t('AppOperationsCenterFieldsPreviewDialog.Área (ha)')" :headerStyle="{width: '8rem'}">
                            <template #body="slotProps">
                                <Tag v-if="!slotProps.data.importable" severity="info"
                                     :value="$t('AppOperationsCenterFieldsPreviewDialog.Sem contorno')"/>
                                <span v-else>{{ slotProps.data.area }}</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <!-- Paginação -->
                <div class="pagination-bar">
                    <Button icon="pi pi-chevron-left" class="p-button-text"
                            :label="$t('AppOperationsCenterFieldsPreviewDialog.Anterior')"
                            :disabled="loading || currentPage === 0" @click="prevPage"/>
                    <span class="page-indicator">
                        {{ $t('AppOperationsCenterFieldsPreviewDialog.Página') }} {{ currentPageNumber }}
                        <span v-if="totalPages"> / {{ totalPages }}</span>
                    </span>
                    <Button icon="pi pi-chevron-right" iconPos="right" class="p-button-text"
                            :label="$t('AppOperationsCenterFieldsPreviewDialog.Próxima')"
                            :disabled="loading || isLastPage" @click="nextPage"/>
                </div>
            </div>
        </div>

        <template #footer>
            <Button :label="$t('AppOperationsCenterFieldsPreviewDialog.Cancelar')" icon="pi pi-times" class="p-button-text"
                    :disabled="importing" @click="$emit('update:visible', false)"/>
            <Button v-if="!notLinked" :label="importLabel" icon="pi pi-check" :loading="importing"
                    :disabled="importing || selectedCount === 0" @click="confirmImport"/>
        </template>
    </Dialog>
</template>

<script>

import gmapsInit from '../../../utils/gmaps';
import * as jsts from 'jsts';

import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';

import GeometryMixin from "@/mixins/GeometryMixin";
import JohnDeereService from "@/services/JohnDeereService";
import FieldsService from "@/services/FieldsService";

const DUPLICATE_COLOR = '#e53935';
const SELECTED_COLOR = '#2e7d32';
const UNSELECTED_COLOR = '#9e9e9e';
const ITEM_LIMIT = 10;

export default {
    mixins: [GeometryMixin],
    components: {Dialog, DataTable, Column, Checkbox, Button, Tag, ProgressSpinner},
    props: {
        visible: {type: Boolean, default: false},
        orgId: {type: [String, Number], default: null}
    },
    data() {
        return {
            johnDeereService: null,
            fieldsService: null,
            rows: [],
            // seleção acumulada entre páginas: { [fieldId]: {id, name, coordinates} }
            selected: {},
            itemLimit: ITEM_LIMIT,
            currentPage: 0,
            total: 0,
            isLastPage: false,
            loading: false,
            importing: false,
            notLinked: false,
            allSelectedOnPage: false,
            google: null,
            map: null,
            polygons: {}
        }
    },
    computed: {
        selectedCount() {
            return Object.keys(this.selected).length;
        },
        selectableCount() {
            return this.rows.filter(r => this.canSelect(r)).length;
        },
        currentPageNumber() {
            return this.currentPage + 1;
        },
        totalPages() {
            if (!this.total) {
                return null;
            }
            return Math.ceil(this.total / this.itemLimit);
        },
        importLabel() {
            return `${this.$t('AppOperationsCenterFieldsPreviewDialog.Importar')} (${this.selectedCount})`;
        },
        // Talhões da página no Operations Center: como um talhão de várias partes vira uma linha por parte,
        // linhas != talhões. Só a primeira parte (partIndex 1) conta como talhão.
        pageFieldCount() {
            return this.rows.filter(r => !r.partIndex || r.partIndex === 1).length;
        },
        // A paginação é a de talhões do Operations Center (total / itemLimit), então a página pode ter mais
        // linhas que itemLimit. Esse resumo explica a diferença em vez de deixar a contagem parecer errada.
        pageSummary() {
            if (!this.rows.length) {
                return '';
            }
            if (this.pageFieldCount === this.rows.length) {
                return `${this.rows.length} ${this.$t('AppOperationsCenterFieldsPreviewDialog.talhões nesta página')}`;
            }
            return `${this.pageFieldCount} ${this.$t('AppOperationsCenterFieldsPreviewDialog.talhões')}`
                + ` / ${this.rows.length} ${this.$t('AppOperationsCenterFieldsPreviewDialog.contornos nesta página')}`;
        }
    },
    methods: {
        async onShow() {
            this.johnDeereService = new JohnDeereService();
            this.fieldsService = new FieldsService();
            this.rows = [];
            this.selected = {};
            this.currentPage = 0;
            this.total = 0;
            this.isLastPage = false;
            this.notLinked = false;
            await this.initMap();
            await this.loadPage(0);
        },
        onHide() {
            this.clearPolygons();
            this.map = null;
        },
        async initMap() {
            try {
                this.google = await gmapsInit();
                const el = this.$refs.previewMap;
                if (!el) {
                    return;
                }
                this.map = new this.google.maps.Map(el, {
                    minZoom: 3, maxZoom: 20, zoom: 4,
                    center: {lat: -14.235, lng: -51.925},
                    mapTypeId: 'hybrid',
                    rotateControl: false, streetViewControl: false, mapTypeControl: false,
                    tilt: 0, disableDefaultUI: true, clickableIcons: false
                });
                this.google.maps.event.trigger(this.map, 'resize');
            } catch (error) {
                console.error(error);
            }
        },
        async loadPage(page) {
            if (!this.orgId) {
                return;
            }
            this.loading = true;
            try {
                const offset = page * this.itemLimit;
                const response = await this.johnDeereService.getOperationsCenterFields(this.orgId, offset, this.itemLimit);
                this.currentPage = page;
                this.total = response.total || 0;
                // backend serializa em snake_case
                this.isLastPage = response.isLastPage !== undefined ? response.isLastPage : response.is_last_page;
                this.buildRows(response.fields || []);
                this.$nextTick(() => this.drawPreview());
                this.checkDuplicatesForPage();
            } catch (error) {
                if (error && error.response && error.response.status === 404) {
                    this.notLinked = true;
                } else {
                    this.$toast.add({
                        severity: 'error', summary: this.$t('AppOperationsCenterFieldsPreviewDialog.Falha'),
                        detail: this.$t('AppOperationsCenterFieldsPreviewDialog.Não foi possível buscar os talhões do Operations Center'),
                        life: 5000
                    });
                }
            } finally {
                this.loading = false;
            }
        },
        buildRows(fields) {
            this.rows = fields.map(f => ({
                id: f.id,
                name: f.name,
                coordinates: f.coordinates,
                // backend serializa em snake_case
                partIndex: f.partIndex !== undefined ? f.partIndex : f.part_index,
                partCount: f.partCount !== undefined ? f.partCount : f.part_count,
                importable: !!f.coordinates,
                duplicate: false,
                selected: !!this.selected[f.id],
                area: '—'
            }));
            this.syncAllSelectedOnPage();
        },
        async checkDuplicatesForPage() {
            const importableRows = this.rows.filter(r => r.importable);
            if (importableRows.length === 0) {
                return;
            }
            try {
                const payload = {fields: importableRows.map(r => ({id: r.id, coordinates: r.coordinates}))};
                const response = await this.fieldsService.checkDuplicatesV3(this.orgId, payload);
                const duplicateIds = (response && (response.duplicateIds || response.duplicate_ids)) || [];
                const dupSet = new Set(duplicateIds);
                this.rows.forEach(row => {
                    if (dupSet.has(row.id)) {
                        row.duplicate = true;
                        if (row.selected) {
                            row.selected = false;
                            this.$delete(this.selected, row.id);
                        }
                        this.updatePolygonStyle(row);
                    }
                });
                this.syncAllSelectedOnPage();
            } catch (error) {
                // duplicidade é informativa; se falhar, apenas não marca (o import ignora duplicados de qualquer forma)
                console.warn('Falha ao checar duplicados do Operations Center', error);
            }
        },
        canSelect(row) {
            return row.importable && !row.duplicate;
        },
        prevPage() {
            if (this.loading || this.currentPage === 0) {
                return;
            }
            this.loadPage(this.currentPage - 1);
        },
        nextPage() {
            if (this.loading || this.isLastPage) {
                return;
            }
            this.loadPage(this.currentPage + 1);
        },
        onSelectionChange(row) {
            if (!this.canSelect(row)) {
                row.selected = false;
                return;
            }
            if (row.selected) {
                this.$set(this.selected, row.id, {id: row.id, name: row.name, coordinates: row.coordinates});
            } else {
                this.$delete(this.selected, row.id);
            }
            this.updatePolygonStyle(row);
            this.syncAllSelectedOnPage();
        },
        toggleAllOnPage() {
            const value = this.allSelectedOnPage;
            this.rows.forEach(row => {
                if (!this.canSelect(row)) {
                    return;
                }
                row.selected = value;
                if (value) {
                    this.$set(this.selected, row.id, {id: row.id, name: row.name, coordinates: row.coordinates});
                } else {
                    this.$delete(this.selected, row.id);
                }
                this.updatePolygonStyle(row);
            });
        },
        syncAllSelectedOnPage() {
            const selectable = this.rows.filter(r => this.canSelect(r));
            this.allSelectedOnPage = selectable.length > 0 && selectable.every(r => r.selected);
        },
        onRowHover(event) {
            const row = event.data;
            const polygon = this.polygons[row.id];
            if (polygon && this.map) {
                const bounds = new this.google.maps.LatLngBounds();
                polygon.getPaths().forEach(path => path.forEach(latlng => bounds.extend(latlng)));
                this.map.panTo(bounds.getCenter());
            }
        },
        rowClass(row) {
            return row.duplicate ? 'row-duplicate' : (!row.importable ? 'row-disabled' : '');
        },
        drawPreview() {
            if (!this.map || !this.google) {
                return;
            }
            this.clearPolygons();
            const reader = new jsts.io.WKTReader();
            const bounds = new this.google.maps.LatLngBounds();
            let hasGeometry = false;

            this.rows.forEach(row => {
                if (!row.coordinates) {
                    return;
                }
                let geometry;
                try {
                    geometry = reader.read(row.coordinates);
                } catch (e) {
                    console.warn('Geometria inválida no preview', row.id, e);
                    return;
                }
                const paths = this.jstsWithHolesToGoogleMaps(geometry, this.google.maps);
                const polygon = new this.google.maps.Polygon({
                    paths: paths,
                    strokeWeight: 2,
                    editable: false,
                    zIndex: 1 / geometry.getArea()
                });
                polygon.setMap(this.map);

                row.area = (this.google.maps.geometry.spherical.computeArea(
                    this.jstsWithoutHolesToGoogleMaps(geometry, this.google.maps)[0]) / 10000).toFixed(2);

                const vm = this;
                this.google.maps.event.addListener(polygon, 'click', function () {
                    if (vm.canSelect(row)) {
                        row.selected = !row.selected;
                        vm.onSelectionChange(row);
                    }
                });

                this.polygons[row.id] = polygon;
                this.applyStyle(polygon, row);

                paths.forEach(path => path.forEach(latlng => {
                    bounds.extend(latlng);
                    hasGeometry = true;
                }));
            });

            if (hasGeometry) {
                this.map.fitBounds(bounds);
            }
        },
        applyStyle(polygon, row) {
            const color = row.duplicate ? DUPLICATE_COLOR : (row.selected ? SELECTED_COLOR : UNSELECTED_COLOR);
            polygon.setOptions({
                strokeColor: color,
                strokeOpacity: 0.9,
                fillColor: color,
                fillOpacity: row.selected ? 0.45 : 0.12
            });
        },
        updatePolygonStyle(row) {
            const polygon = this.polygons[row.id];
            if (polygon) {
                this.applyStyle(polygon, row);
            }
        },
        clearPolygons() {
            Object.values(this.polygons).forEach(p => p.setMap(null));
            this.polygons = {};
        },
        confirmImport() {
            if (this.selectedCount === 0) {
                return;
            }
            const fields = Object.values(this.selected).map(f => ({
                name: f.name || '',
                coordinates: f.coordinates,
                type: 'PRODUCTIVE'
            }));

            this.importing = true;
            this.fieldsService.importFieldsV3(this.orgId, {fields})
                .then(response => {
                    const imported = response && response.imported ? response.imported : 0;
                    const skipped = response && response.skipped ? response.skipped : 0;
                    this.$toast.add({
                        severity: 'success', summary: this.$t('AppOperationsCenterFieldsPreviewDialog.Sucesso'),
                        detail: this.$t('AppOperationsCenterFieldsPreviewDialog.talhões importados', {imported, skipped}),
                        life: 5000
                    });
                    this.$emit('imported');
                    this.$emit('update:visible', false);
                }).catch(() => {
                this.$toast.add({
                    severity: 'error', summary: this.$t('AppOperationsCenterFieldsPreviewDialog.Falha'),
                    detail: this.$t('AppOperationsCenterFieldsPreviewDialog.Não foi possível importar os talhões'), life: 4000
                });
            }).finally(() => {
                this.importing = false;
            })
        }
    }
}
</script>

<style scoped lang="scss">
.preview-body {
    display: flex;
    height: 100%;
    width: 100%;
}

.preview-map-wrapper {
    flex: 1 1 55%;
    min-width: 0;
    height: 100%;
    position: relative;
}

#oc-preview-map {
    width: 100%;
    height: 100%;
}

.map-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.35);
}

.preview-controls {
    flex: 1 1 45%;
    display: flex;
    flex-direction: column;
    padding: 16px;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
}

/* A lista rola aqui dentro para a barra de paginação não sair da área visível quando a página
   tem muitas linhas (talhão de várias partes rende uma linha por parte). */
.preview-table-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
}

.controls-summary {
    margin: 8px 0;
    font-size: 13px;
    color: #4b4b4b;
}

.total-summary {
    color: #6b6b6b;
}

.preview-table {
    flex: 0 0 auto;
}

.part-hint {
    margin-left: 8px;
    color: #9e9e9e;
}

::v-deep .row-duplicate {
    background-color: #fff3f0 !important;
    color: #9e9e9e;
}

::v-deep .row-disabled {
    color: #9e9e9e;
}

.ml-2 {
    margin-left: 8px;
}

.pagination-bar {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e0e0e0;
}

.page-indicator {
    font-size: 13px;
    color: #4b4b4b;
}

.oc-message {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #4b4b4b;
    gap: 6px;
}
</style>
